'use client';

import { useState, useCallback, useEffect, useMemo } from 'react';
import moment from 'moment';
import { LocalStorage } from '@/src/utils/general/local-storage';
import { useClicksterStore } from '@/src/stores';
import { ValidDateFormatsEnum, getTimeZones, generateReportTemplate } from '@/src/utils';
import type { FilterState } from '../types';
import type ITimeZone from '@/src/components/schedule/types/timezone';

// LocalStorage key per DASHBOARD_ARCHITECTURE.md
const STORAGE_KEY = 'dashboard-filters';

/**
 * useDashboardFilters - Manages dashboard filter state
 *
 * Extracted from dashboard-overview.tsx to enable shared state across
 * dashboard sub-routes via context.
 *
 * Features:
 * - Initializes from localStorage if available
 * - Falls back to user timezone from store
 * - Persists changes to localStorage
 * - Provides convenient handleFilterChange for partial updates
 */
export function useDashboardFilters() {
  const { userInSession } = useClicksterStore((state) => state);

  // Get user's timezone with fallback to EST
  const userTimezone = useMemo(() => {
    const timezonesWithDST = getTimeZones();
    const defaultTimezone = timezonesWithDST.find((tz: ITimeZone) => tz.moment_name === 'EST5EDT') || {
      text: 'EST Eastern Standard Time GMT-5:00',
      default_value: 5,
      moment_name: 'EST5EDT',
      isDST: false,
      value: 5
    };

    if (!userInSession?.timeZone) return defaultTimezone;

    const foundTimezone = timezonesWithDST.find((tz: ITimeZone) => tz.moment_name === userInSession.timeZone);

    return foundTimezone || defaultTimezone;
  }, [userInSession?.timeZone]);

  // Create default filters based on user timezone
  const getDefaultFilters = useCallback((): FilterState => ({
    mediaBuyers: [],
    trafficSources: [],
    categories: [],
    campaigns: [],
    offers: [],
    affiliates: [],
    dateRange: {
      start: moment().startOf('day').format(ValidDateFormatsEnum.YYYY_MM_DD_HH_MM_SS),
      end: moment().endOf('day').format(ValidDateFormatsEnum.YYYY_MM_DD_HH_MM_SS),
    },
    timezone: userTimezone,
    dates: {
      from: moment().startOf('day').format(ValidDateFormatsEnum.YYYY_MM_DD_HH_MM_SS),
      to: moment().endOf('day').format(ValidDateFormatsEnum.YYYY_MM_DD_HH_MM_SS),
      moment_name: userTimezone.moment_name,
      time_offset: userTimezone.value || 0,
      weekdays: false,
      weekends: false
    },
    compareDates: {
      dates: [new Date(), new Date()],
      data: [],
      isLoading: false,
      isApplied: false
    },
    report: generateReportTemplate([], userTimezone),
    includeOfferReport: false
  }), [userTimezone]);

  const [filters, setFiltersState] = useState<FilterState>(() => {
    // Try to restore from localStorage first
    const stored = LocalStorage.get(STORAGE_KEY);
    if (stored) {
      try {
        // Merge stored filters with defaults to ensure all fields exist
        return { ...getDefaultFilters(), ...stored };
      } catch {
        // If parsing fails, use defaults
      }
    }
    return getDefaultFilters();
  });

  // Wrapper for setFilters that handles both direct values and updater functions
  const setFilters = useCallback((newFilters: FilterState | ((prev: FilterState) => FilterState)) => {
    setFiltersState(prev => {
      const updated = typeof newFilters === 'function' ? newFilters(prev) : newFilters;
      return updated;
    });
  }, []);

  // Convenience method for partial filter updates (used by filter panel)
  const handleFilterChange = useCallback((newFilters: FilterState) => {
    const start = newFilters.dateRange.start;
    const end = newFilters.dateRange.end;

    const updatedFilters = {
      ...newFilters,
      dateRange: {
        start,
        end,
      },
      dates: {
        ...newFilters.dates,
        from: start,
        to: end,
      },
    };

    setFilters(updatedFilters);
  }, [setFilters]);

  // Force reload by updating timestamp
  const handleReload = useCallback(() => {
    setFilters(currentFilters => ({
      ...currentFilters,
      reloadTimestamp: Date.now()
    }));
  }, [setFilters]);

  // Persist to localStorage whenever filters change
  useEffect(() => {
    LocalStorage.set(STORAGE_KEY, filters);
  }, [filters]);

  // Computed: count of active filters
  const activeFiltersCount = useMemo(() => {
    const { mediaBuyers, trafficSources, categories, campaigns, offers, affiliates } = filters;
    return mediaBuyers.length + trafficSources.length + categories.length +
           campaigns.length + offers.length + affiliates.length;
  }, [filters]);

  // Computed: check if date range is today
  const isDateRangeToday = useMemo(() => {
    if (!filters?.dates) return true;

    const today = moment().format('YYYY/MM/DD');
    const fromDate = moment(filters.dates.from, ['YYYY-MM-DD HH:mm:ss', 'YYYY/MM/DD HH:mm:ss']).format('YYYY/MM/DD');
    const toDate = moment(filters.dates.to, ['YYYY-MM-DD HH:mm:ss', 'YYYY/MM/DD HH:mm:ss']).format('YYYY/MM/DD');

    return fromDate === today && toDate === today;
  }, [filters?.dates]);

  return {
    filters,
    setFilters,
    handleFilterChange,
    handleReload,
    activeFiltersCount,
    isDateRangeToday,
    userTimezone,
  };
}

export type UseDashboardFiltersReturn = ReturnType<typeof useDashboardFilters>;
